import { useCanvasStore } from '../stores/canvasStore.js';
import {
  buildProfileCanvasPlan,
  PROFILE_SECOND_ORDER_LAYOUTS,
  PROFILE_WIDGET_LAYOUTS,
} from './profileCanvasPlan.js';

export interface EnsureProfileDashboardOptions {
  profileId: string;
  profileName?: string;
  ticker?: string;
}

/**
 * Ensures the Profile v2 dashboard canvas is deterministic for a given profile:
 * - Enforces the canonical set of widgets (singles + 6 second-order summaries)
 * - Removes duplicates and foreign widgets for other profiles
 * - Cleans dangling connections
 * - Normalizes config to carry the correct profileId/ticker
 *
 * Note: Subcanvas groups (widgetGroup) are preserved.
 */
export function ensureProfileDashboardCanvas(
  canvasId: string,
  opts: EnsureProfileDashboardOptions
): void {
  const store = useCanvasStore.getState();
  const { profileId, profileName, ticker } = opts;
  const canvas = store.canvases.find((c) => c.id === canvasId);
  if (!canvas) return;

  const expected = buildProfileCanvasPlan({ profileId, profileName, ticker });
  const expectedWidgets = (expected.plan?.widgets || []) as Array<{
    type: string;
    position?: { x: number; y: number };
    config?: Record<string, any>;
  }>;

  const secondOrderLayoutMap = new Map(
    PROFILE_SECOND_ORDER_LAYOUTS.map((entry) => [String(entry.domain), entry])
  );

  // Classify expected widgets
  const singles = new Map<string, typeof expectedWidgets[number]>();
  expectedWidgets.forEach((w) => {
    if (w.type === 'akemIntelligence') {
      // handled separately below
    } else {
      if (!singles.has(w.type)) singles.set(w.type, w);
    }
  });

  const keepIds = new Set<string>();

  // 1) Ensure singles exist exactly once and normalized
  for (const [type, w] of singles.entries()) {
    // Only match widgets explicitly scoped to this profileId
    const ex = (canvas.widgets || []).find(
      (x) => x.type === type && (x.config as any)?.profileId === profileId
    );
    if (ex) {
      keepIds.add(ex.id);
      const layout = PROFILE_WIDGET_LAYOUTS[type];
      const cfg = { ...(ex.config || {}), ...(w.config || {}), profileId } as any;
      store.updateWidgetConfig(canvasId, ex.id, cfg);
      if (layout) {
        store.moveWidget(canvasId, ex.id, layout.position);
        store.resizeWidget(canvasId, ex.id, layout.size);
      } else if (w.position) {
        store.moveWidget(canvasId, ex.id, w.position);
      }
    } else {
      const layout = PROFILE_WIDGET_LAYOUTS[type];
      const position = layout?.position || w.position || { x: 80, y: 80 };
      const id = store.addWidgetToCanvas(canvasId, type, position, {
        ...(w.config || {}),
        profileId,
      });
      keepIds.add(id);
      if (layout) {
        store.resizeWidget(canvasId, id, layout.size);
      }
    }
  }

  // 2) Ensure Akem Intelligence (6) — one per expected domain
  const seen = new Set<string>();
  for (const w of expectedWidgets) {
    if (w.type !== 'akemIntelligence') continue;
    const domain = String((w.config as any)?.domain || '');
    if (!domain || seen.has(domain)) continue;
    const ex = (canvas.widgets || []).find(
      (x) => x.type === 'akemIntelligence' && (x.config as any)?.domain === domain
    );
    if (ex) {
      keepIds.add(ex.id);
      const layout = secondOrderLayoutMap.get(domain);
      const cfg = { ...(ex.config || {}), ...(w.config || {}), profileId } as any;
      store.updateWidgetConfig(canvasId, ex.id, cfg);
      if (layout) {
        store.moveWidget(canvasId, ex.id, layout.position);
        store.resizeWidget(canvasId, ex.id, layout.size);
      } else if (w.position) {
        store.moveWidget(canvasId, ex.id, w.position);
      }
    } else {
      const layout = secondOrderLayoutMap.get(domain);
      const position = layout?.position || w.position || { x: 80, y: 80 };
      const id = store.addWidgetToCanvas(canvasId, 'akemIntelligence', position, {
        ...(w.config || {}),
        profileId,
      });
      keepIds.add(id);
      if (layout) {
        store.resizeWidget(canvasId, id, layout.size);
      }
    }
    seen.add(domain);
  }

  // 3) Remove everything else except subcanvas groups
  const current = useCanvasStore.getState().canvases.find((c) => c.id === canvasId);
  if (current) {
    const toRemove = current.widgets
      .filter((w) => {
        if (w.type === 'widgetGroup') return false; // preserve subcanvases
        // Remove if not explicitly kept, or belongs to a different profile
        const pid = (w.config as any)?.profileId;
        if (pid && pid !== profileId) return true;
        return !keepIds.has(w.id);
      })
      .map((w) => w.id);
    toRemove.forEach((wid) => {
      try {
        store.removeWidget(canvasId, wid);
      } catch {}
    });

    // 4) Clean dangling connections
    const refreshed = useCanvasStore.getState().canvases.find((c) => c.id === canvasId);
    if (refreshed) {
      const validIds = new Set(refreshed.widgets.map((w) => w.id));
      const dangling = refreshed.connections.filter(
        (c) => !validIds.has(c.source.widgetId) || !validIds.has(c.target.widgetId)
      );
      dangling.forEach((conn) => {
        try { store.removeConnection(canvasId, conn.id); } catch {}
      });
    }
  }
}
